import { jsPDF } from "jspdf";
import { autoTable } from "jspdf-autotable";

const NOTO_TTF_URL =
  "https://cdn.jsdelivr.net/gh/googlefonts/noto-fonts@main/hinted/ttf/NotoSans/NotoSans-Regular.ttf";
const FONT_NAME = "NotoSans";

let fontBase64Promise = null;

function arrayBufferToBase64(buffer) {
  let binary = "";
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < bytes.byteLength; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

async function loadNotoFontBase64() {
  if (!fontBase64Promise) {
    fontBase64Promise = fetch(NOTO_TTF_URL)
      .then((r) => {
        if (!r.ok) throw new Error(`Font HTTP ${r.status}`);
        return r.arrayBuffer();
      })
      .then(arrayBufferToBase64);
  }
  return fontBase64Promise;
}

function money(v) {
  return Number(v || 0).toLocaleString("vi-VN", {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  });
}

function dateCell(v) {
  const s = String(v || "").slice(0, 10);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : s || "—";
}

async function createDoc(title, periodLabel) {
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  try {
    const base64 = await loadNotoFontBase64();
    doc.addFileToVFS(`${FONT_NAME}.ttf`, base64);
    doc.addFont(`${FONT_NAME}.ttf`, FONT_NAME, "normal");
    doc.setFont(FONT_NAME);
  } catch {
    // fallback default font
  }
  doc.setFontSize(14);
  doc.text(title, 14, 16);
  doc.setFontSize(10);
  doc.text(periodLabel, 14, 22);
  return doc;
}

function saveDoc(doc, filename) {
  doc.save(filename);
}

/**
 * @param {object} data
 * @param {{ filename?: string }} [opts]
 */
export async function exportCashflowPdf(data, opts = {}) {
  const from = data?.filters?.from_date || "";
  const to = data?.filters?.to_date || "";
  const filename = opts.filename || `bao_cao_dong_tien_${from}_${to}.pdf`;
  const doc = await createDoc(
    "Báo cáo dòng tiền thu–chi",
    `Kỳ: ${dateCell(from)} – ${dateCell(to)} | Thu: ${money(
      data?.summary?.total_in
    )} | Chi: ${money(data?.summary?.total_out)} | Net: ${money(data?.summary?.net)}`
  );

  autoTable(doc, {
    startY: 28,
    head: [["Ngày", "Loại", "Mã", "Mô tả", "Chi nhánh", "Thu", "Chi"]],
    body: (data?.ledger || []).map((row) => [
      dateCell(row.entry_date),
      row.entry_type_label || "",
      row.code || "",
      row.description || "",
      row.branch_name || "",
      money(row.amount_in),
      money(row.amount_out),
    ]),
    styles: { font: FONT_NAME, fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [185, 28, 28], textColor: 255, fontStyle: "bold" },
    margin: { left: 14, right: 14 },
  });

  saveDoc(doc, filename);
}

/**
 * @param {object} data
 * @param {{ filename?: string }} [opts]
 */
export async function exportCollectionsPdf(data, opts = {}) {
  const from = data?.filters?.from_date || "";
  const to = data?.filters?.to_date || "";
  const filename = opts.filename || `bao_cao_thu_cong_no_${from}_${to}.pdf`;
  const doc = await createDoc(
    "Báo cáo thu tiền & công nợ",
    `Kỳ: ${dateCell(from)} – ${dateCell(to)} | Thu: ${money(
      data?.summary?.receipt_total
    )} | HĐ chưa thu: ${money(data?.summary?.unpaid_total)}`
  );

  autoTable(doc, {
    startY: 28,
    head: [["Nguồn", "Số phiếu", "Tổng tiền"]],
    body: (data?.receipt_summary || []).map((r) => [
      r.payer_type_label,
      String(r.count),
      money(r.total),
    ]),
    styles: { font: FONT_NAME, fontSize: 9, cellPadding: 2 },
    headStyles: { fillColor: [185, 28, 28], textColor: 255 },
    margin: { left: 14, right: 14 },
  });

  let y = (doc.lastAutoTable?.finalY || 40) + 8;
  doc.setFontSize(11);
  doc.text("Phiếu thu chi tiết", 14, y);
  y += 2;

  autoTable(doc, {
    startY: y + 2,
    head: [["Ngày", "Mã", "Nguồn", "Người nộp", "Chi nhánh", "Số tiền"]],
    body: (data?.receipts || []).map((r) => [
      dateCell(r.receipt_date),
      r.receipt_code || "",
      r.payer_type_label || "",
      r.payer_name || "",
      r.branch_name || "",
      money(r.amount),
    ]),
    styles: { font: FONT_NAME, fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [185, 28, 28], textColor: 255 },
    margin: { left: 14, right: 14 },
  });

  y = (doc.lastAutoTable?.finalY || y) + 8;
  if (y > 180) {
    doc.addPage();
    y = 16;
  }
  doc.setFontSize(11);
  doc.text("Hóa đơn bán chưa thu", 14, y);

  autoTable(doc, {
    startY: y + 2,
    head: [["Ngày", "Số HĐ", "Đối tác", "Chi nhánh", "Số tiền", "Trạng thái", "Ngày treo"]],
    body: (data?.unpaid_invoices || []).map((r) => [
      dateCell(r.invoice_date),
      r.invoice_number || "",
      r.partner_name || "",
      r.branch_name || "",
      money(r.total_amount),
      r.processing_status_label || r.processing_status || "",
      String(r.days_outstanding ?? 0),
    ]),
    styles: { font: FONT_NAME, fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [185, 28, 28], textColor: 255 },
    margin: { left: 14, right: 14 },
  });

  saveDoc(doc, filename);
}
